import { ISmallSite } from '~/src/dash/api/channels/channels_models';
import { IChannelGroup, INameID, IChannelList } from '~/src/dash/api/console-api';
import { addQueryParams, createUrl, restGet } from '~/src/dash/api/rest';
import { EActive } from '~/src/dash/api/types/active';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { checkUserInfoMasked } from '~/src/dash/utils/roles';

export async function fetchSmallSites(publisherID: string): Promise<ISmallSite[]> {
  const reqUrl = createUrl('/small_sites', publisherID, 'v2');
  const response = await restGet<any>(reqUrl, {});
  return response.parsedBody;
}

// generates active channels
export const generateChannelsOptionsList = (
  session: ISessionState,
  user: IUserState,
  isIDNumber: boolean,
): Promise<ISelectOption[]> => {
  const shouldMaskLabel = checkUserInfoMasked(user.user);
  return fetchSmallSites(session.publisher.ID)
    .then((channels) => {
      const options: ISelectOption[] = [];
      channels.forEach((channel) => {
        if (channel.Active === EActive.ACTIVE) {
          options.push({
            id: isIDNumber ? channel.ID : String(channel.ID),
            label: shouldMaskLabel ? `demo_site_${channel.ID}` : channel.Name,
            isActive: true,
          });
        }
      });
      return options;
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export async function fetchDealTargetedChannels(
  publisherID: string,
  dealID: number,
): Promise<INameID[]> {
  const reqUrl = createUrl(`/publishers/${publisherID}/deal/${dealID}/channels`, '', 'v1');
  const urlWithParams = addQueryParams(reqUrl, { get_all: true });
  const response = await restGet<{ channels: INameID[] }>(urlWithParams);
  return response.parsedBody?.channels || [];
}

export async function fetchDealTargetedChannelGroups(
  publisherID: string,
  dealID: number,
): Promise<INameID[]> {
  const reqUrl = createUrl(`/publishers/${publisherID}/deal/${dealID}/channel_groups`, '', 'v1');
  const urlWithParams = addQueryParams(reqUrl, { get_all: true });
  const response = await restGet<{ channelGroups: INameID[] }>(urlWithParams);
  return response.parsedBody?.channelGroups || [];
}

export async function fetchSimpleChannelGroups(publisherID: string): Promise<IChannelGroup[]> {
  const reqUrl = createUrl('/channel_groups', publisherID, 'v1');
  const response = await restGet<any>(reqUrl, {});
  return response.parsedBody.ChannelGroups;
}

export const generateChannelGroupsOptionsList = (
  session: ISessionState,
  user: IUserState,
): Promise<ISelectOption[]> => {
  const shouldMaskLabel = checkUserInfoMasked(user.user);
  return fetchSimpleChannelGroups(session.publisher.ID)
    .then((cgs) => {
      const options: ISelectOption[] = [];
      cgs.forEach((cg) => {
        const numSites = (cg.SitesInfo || []).length || 0;
        const numSitesString = `(${numSites} Site${numSites !== 1 ? 's' : ''})`;
        options.push({
          // @ts-ignore
          id: cg.id, // TODO: refactor needed. the backend returns this as 'id' but the ui interface has it as 'ID'
          // @ts-ignore
          label: (shouldMaskLabel ? `demo_channel_group_${cg.id}` : cg.Name) + ` ${numSitesString}`,
        });
      });
      return options;
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export async function fetchChannelLists(publisherID: string): Promise<IChannelList[]> {
  const reqUrl = createUrl('/channel_lists', publisherID, 'v1');
  const response = await restGet<any>(reqUrl, {});
  return response.parsedBody;
}

const countItemsInJointString = (string: string): number => {
  if (string) {
    return string.split(',').length;
  } else {
    return 0;
  }
};

const createChannelListLabelSuffix = (
  numChannels: number,
  channelString: string,
  numGroups: number,
  groupString: string,
): string => {
  switch (true) {
    case numChannels > 0 && numGroups > 0:
      return `(${channelString}, ${groupString})`;
    case numChannels > 0:
      return `(${channelString})`;
    case numGroups > 0:
      return `(${groupString})`;
    default:
      return '';
  }
};

export const generateChannelListsOptionsList = (
  session: ISessionState,
  user: IUserState,
): Promise<ISelectOption[]> => {
  const shouldMaskLabel = checkUserInfoMasked(user.user);
  return fetchChannelLists(session.publisher.ID)
    .then((cls) => {
      const options: ISelectOption[] = [];
      cls.forEach((cl) => {
        const numSites = cl?.ChannelIDs ? countItemsInJointString(cl.ChannelIDs) : 0;
        const numSitesString = `${numSites} Channel${numSites !== 1 ? 's' : ''}`;
        const numGroups = cl?.ChannelGroupIDs ? countItemsInJointString(cl.ChannelGroupIDs) : 0;
        const numGroupsString = `${numGroups} Channel Group${numGroups !== 1 ? 's' : ''}`;
        const suffix = createChannelListLabelSuffix(
          numSites,
          numSitesString,
          numGroups,
          numGroupsString,
        );
        options.push({
          // @ts-ignore
          id: cl.id, // TODO: refactor needed. the backend returns this as 'id' but the ui interface has it as 'ID'
          // @ts-ignore
          label: (shouldMaskLabel ? `demo_channel_list_${cl.id}` : cl.Name) + ` ${suffix}`,
        });
      });
      return options;
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};
